import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Chip, Stack, Typography } from '@mui/material';
import ArcadeGameShell from '../ArcadeGameShell';
import { useArcadeAgentSession } from '../useArcadeAgentSession';
import { ManualPlayChrome, useManualPlay, usePlayKeyboard } from '../manualPlay';
import { GAME_BOILERPLATE } from './AGENT_BOILERPLATE';
import { ARCADE } from '../../../utils/config';
import { useManualRunRecorder } from '../useManualRunRecorder';
import {
    createNumberlinkSession,
    deleteNumberlinkSession,
    moveNumberlinkSession,
    SEGMENT_POINTS,
    PAIR_BONUS,
    SOLVE_BONUS,
    computeNumberlinkScore,
} from './numberlinkGmClient';
import { useTransientMessage } from '../useTransientMessage';
import { gmStartErrorMessage } from '../gmLease';
import { COLOR, GAME_ACCENT } from '../ui/tokens';

const GAME_ID = 'numberlink';
const { UPLOAD_URL } = ARCADE.numberlink;
const ACCENT = GAME_ACCENT.numberlink;
const BOARD_SIZE = 5;
const PAIR_ORDER = ['1', '2', '3', '4'];
const PAIR_COLORS = ['#FF7043', '#42A5F5', '#66BB6A', '#AB47BC'];
const HINT = '1–4 select pair · Arrows / WASD draw · Backspace / U undo · Esc pause';

export { SEGMENT_POINTS, PAIR_BONUS, SOLVE_BONUS, computeNumberlinkScore };

const blankPath = () => Array.from({ length: BOARD_SIZE }, () => Array(BOARD_SIZE).fill(''));
const blankBase = () => Array.from({ length: BOARD_SIZE }, () => Array(BOARD_SIZE).fill(''));

const Numberlink = () => {
    const play = useManualPlay();
    const sessionIdRef = useRef(null);
    const [baseBoard, setBaseBoard] = useState(blankBase);
    const [pathBoard, setPathBoard] = useState(blankPath);
    const [completed, setCompleted] = useState(() => new Set());
    const [moves, setMoves] = useState(0);
    const [score, setScore] = useState(0);
    const [isSolved, setIsSolved] = useState(false);
    const [selectedPair, setSelectedPair] = useState('1');
    const [cursor, setCursor] = useState({ row: 0, col: 0 });
    const [message, setMessage] = useTransientMessage();
    const [busy, setBusy] = useState(false);

    const session = useArcadeAgentSession({ gameId: GAME_ID, uploadUrl: UPLOAD_URL });
    const recorder = useManualRunRecorder({ gameId: GAME_ID, session });

    const pairColorMap = useMemo(() =>
        PAIR_ORDER.reduce((acc, pair, idx) => {
            acc[pair] = PAIR_COLORS[idx % PAIR_COLORS.length];
            return acc;
        }, {}), []);

    const applyGm = useCallback((data) => {
        if (Array.isArray(data.base_board)) setBaseBoard(data.base_board.map((r) => r.slice()));
        if (Array.isArray(data.path_board)) setPathBoard(data.path_board.map((r) => r.slice()));
        if (typeof data.moves === 'number') setMoves(data.moves);
        if (typeof data.score === 'number') setScore(data.score);
        setIsSolved(Boolean(data.is_solved));
        setCompleted(new Set(data.completed_pairs || []));
        setMessage(data.message || '');
        return {
            over: Boolean(data.is_solved || data.is_game_over),
            score: typeof data.score === 'number' ? data.score : 0,
            pairsCompleted: typeof data.pairs_completed === 'number' ? data.pairs_completed : 0,
        };
    }, [setMessage]);

    const initFromGm = useCallback(async () => {
        if (sessionIdRef.current) {
            await deleteNumberlinkSession(sessionIdRef.current);
            sessionIdRef.current = null;
        }
        const data = await createNumberlinkSession();
        if (!data?.session_id) throw new Error('Numberlink GM missing session_id');
        sessionIdRef.current = data.session_id;
        applyGm(data);
        setSelectedPair('1');
        const ends = data.pairs?.[0]?.endpoints?.[0];
        if (Array.isArray(ends)) setCursor({ row: ends[0], col: ends[1] });
        else setCursor({ row: 0, col: 0 });
        recorder.reset({
            base_board: data.base_board,
            init_state: { base_board: data.base_board },
        });
    }, [applyGm, recorder]);

    const startGame = useCallback(async () => {
        try {
            setBusy(true);
            await initFromGm();
            play.startGame();
        } catch (err) {
            console.error(err);
            setMessage(gmStartErrorMessage(err));
        } finally {
            setBusy(false);
        }
    }, [initFromGm, play, setMessage]);

    const endGame = useCallback(async () => {
        const sid = sessionIdRef.current;
        sessionIdRef.current = null;
        await deleteNumberlinkSession(sid);
        play.endGame({ score });
        setPathBoard(blankPath());
        setCompleted(new Set());
        setMoves(0);
        setScore(0);
        setIsSolved(false);
        setSelectedPair('1');
        setMessage('');
    }, [play, setMessage, score]);

    useEffect(() => () => {
        const sid = sessionIdRef.current;
        sessionIdRef.current = null;
        deleteNumberlinkSession(sid);
    }, []);

    useEffect(() => {
        // When selecting a pair locally, jump cursor to first endpoint from base board.
        for (let r = 0; r < BOARD_SIZE; r++) {
            for (let c = 0; c < BOARD_SIZE; c++) {
                if (baseBoard[r]?.[c] === selectedPair) {
                    setCursor({ row: r, col: c });
                    return;
                }
            }
        }
    }, [selectedPair, baseBoard]);

    const drawAt = useCallback(async (row, col) => {
        if (play.phase !== 'playing' || isSolved || busy) return;
        const sid = sessionIdRef.current;
        if (!sid) return;
        setBusy(true);
        try {
            const data = await moveNumberlinkSession(sid, {
                action: 'draw',
                pair: selectedPair,
                row,
                col,
            });
            const meta = applyGm(data);
            recorder.recordTick({
                action: 'draw',
                row,
                col,
                pair: selectedPair,
                path_board: data.path_board,
                pairs_completed: meta.pairsCompleted,
                score: meta.score,
            });
            if (meta.over) {
                play.markOver();
                recorder.finalizeAndUpload({
                    score: meta.score,
                    extra: { pairs_completed: PAIR_ORDER.length, base_board: data.base_board },
                });
                const done = sessionIdRef.current;
                sessionIdRef.current = null;
                deleteNumberlinkSession(done);
            }
        } catch (err) {
            console.warn('Numberlink draw failed', err);
            setMessage(err?.data?.message || 'Draw failed');
        } finally {
            setBusy(false);
        }
    }, [applyGm, busy, isSolved, play, recorder, selectedPair, setMessage]);

    const undo = useCallback(async () => {
        if (play.phase !== 'playing' || isSolved || busy) return;
        const sid = sessionIdRef.current;
        if (!sid) return;
        setBusy(true);
        try {
            const data = await moveNumberlinkSession(sid, {
                action: 'undo',
                pair: selectedPair,
            });
            const meta = applyGm(data);
            const last = Array.isArray(data.history) ? data.history[data.history.length - 1] : null;
            recorder.recordTick({
                action: 'undo',
                row: last?.row,
                col: last?.col,
                pair: selectedPair,
                pairs_completed: meta.pairsCompleted,
                score: meta.score,
            });
        } catch (err) {
            console.warn('Numberlink undo failed', err);
            setMessage(err?.data?.message || 'Undo failed');
        } finally {
            setBusy(false);
        }
    }, [applyGm, busy, isSolved, play.phase, recorder, selectedPair, setMessage]);

    const onKey = useCallback((e) => {
        if (['1', '2', '3', '4'].includes(e.key)) {
            setSelectedPair(e.key);
            e.preventDefault();
            return;
        }
        if (e.key === 'Backspace' || e.key === 'u' || e.key === 'U') {
            undo();
            e.preventDefault();
            return;
        }
        if (e.key === 'Enter' || e.key === ' ') {
            drawAt(cursor.row, cursor.col);
            e.preventDefault();
            return;
        }
        let next = { ...cursor };
        if (['ArrowUp', 'w', 'W'].includes(e.key)) next.row = Math.max(0, cursor.row - 1);
        else if (['ArrowDown', 's', 'S'].includes(e.key)) next.row = Math.min(BOARD_SIZE - 1, cursor.row + 1);
        else if (['ArrowLeft', 'a', 'A'].includes(e.key)) next.col = Math.max(0, cursor.col - 1);
        else if (['ArrowRight', 'd', 'D'].includes(e.key)) next.col = Math.min(BOARD_SIZE - 1, cursor.col + 1);
        else return;
        setCursor(next);
        drawAt(next.row, next.col);
        e.preventDefault();
    }, [cursor, drawAt, undo]);

    usePlayKeyboard({
        phase: play.phase,
        togglePause: play.togglePause,
        onKey,
        deps: [cursor, drawAt, undo, selectedPair],
    });

    return (
        <ArcadeGameShell
            title="Numberlink"
            titleAccent="Paths"
            subtitle="Connect matching numbers without crossing paths"
            accentColor={ACCENT}
            boilerplate={GAME_BOILERPLATE.numberlink}
            session={session}
            formatScore={(e) => String(e.score ?? e.pairs_completed ?? 0)}
        >
            <ManualPlayChrome
                phase={play.phase}
                result={play.result}
                keyboardStart={false}
                onStart={startGame}
                onPause={play.togglePause}
                onEnd={endGame}
                accentColor={ACCENT}
                controlsHint={HINT}
            >
                <Box sx={{ p: 2 }}>
                    <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
                        <Chip label={`Moves: ${moves}`} size="small" sx={{ color: ACCENT, borderColor: ACCENT }} variant="outlined" />
                        <Chip label={`Score: ${score}`} size="small" variant="outlined" sx={{ color: COLOR.textMuted, borderColor: COLOR.borderStrong }} />
                        <Chip label={isSolved ? 'Solved' : 'In Progress'} size="small" color={isSolved ? 'success' : 'warning'} />
                    </Stack>
                    {message && <Typography sx={{ color: COLOR.textMuted, mb: 1, fontSize: '0.85rem' }}>{message}</Typography>}
                    <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 1 }}>Select pair (1–4)</Typography>
                    <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
                        {PAIR_ORDER.map((pair) => (
                            <Chip
                                key={pair}
                                label={`${pair}${completed.has(pair) ? ' ✓' : ''}`}
                                onClick={() => setSelectedPair(pair)}
                                size="small"
                                sx={{
                                    color: pairColorMap[pair],
                                    borderColor: selectedPair === pair ? pairColorMap[pair] : COLOR.borderStrong,
                                    bgcolor: selectedPair === pair ? 'rgba(255,255,255,0.06)' : 'transparent',
                                }}
                                variant="outlined"
                            />
                        ))}
                    </Stack>
                    <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: `repeat(${BOARD_SIZE}, 1fr)`,
                        gap: 0.75,
                        p: 1.5,
                        maxWidth: 400,
                        mx: 'auto',
                        borderRadius: 2,
                        border: '1px solid rgba(255,112,67,0.3)',
                    }}
                    >
                        {baseBoard.map((row, rIdx) => row.map((baseValue, cIdx) => {
                            const pathValue = pathBoard[rIdx]?.[cIdx] || '';
                            const isEndpoint = Boolean(baseValue);
                            const focused = play.isActive && cursor.row === rIdx && cursor.col === cIdx;
                            const cellColor = pathValue
                                ? (pairColorMap[pathValue] || '#42A5F5')
                                : isEndpoint ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.25)';
                            return (
                                <Box
                                    key={`${rIdx}-${cIdx}`}
                                    onClick={() => {
                                        if (play.phase !== 'playing' || busy) return;
                                        setCursor({ row: rIdx, col: cIdx });
                                        drawAt(rIdx, cIdx);
                                    }}
                                    sx={{
                                        aspectRatio: '1',
                                        borderRadius: 1.5,
                                        background: cellColor,
                                        border: focused
                                            ? `2px solid ${ACCENT}`
                                            : isEndpoint ? '2px solid rgba(255,255,255,0.35)' : '1px solid rgba(255,255,255,0.12)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: pathValue ? '#0D1117' : 'rgba(255,255,255,0.85)',
                                        fontWeight: 'bold',
                                        cursor: isSolved || play.phase !== 'playing' ? 'default' : 'pointer',
                                    }}
                                >
                                    {baseValue || pathValue || ''}
                                </Box>
                            );
                        }))}
                    </Box>
                </Box>
            </ManualPlayChrome>
        </ArcadeGameShell>
    );
};

export default Numberlink;
